// 同意文言一覧
import React, { useState, useEffect } from 'react';
import { render } from 'react-dom';
import { Row, Col, Button } from 'react-bootstrap';
import axios from '../../axios';
import Moment from 'moment';
import DatePicker from 'react-datepicker';
import ja from 'date-fns/locale/ja';
import MaintenanceTable from '../../organisms/MaintenanceTable';
import LoadingModal from '../../molecules/LoadingModal';
import Messages from '../../molecules/Messages';
import { handleBrowserBack } from '../../BrowserBack';
import {
  date_search_target,
  date_search_type,
  search_target,
  search_type,
  search_params_init,
  status,
  datatable_columns,
  init_error_message,
  customer_document_status,
} from '../../const/CustomerDocumentList';
import { KWANKGM10050A02 } from '../../const/api_modules';
import { ResultList, dataKWANKGM10050A02, detailKWANKGM10050A02 } from '../../types/CustomerDocumentList';
import { KeyValue } from '../../types/KeyValue';
import { api_response_type, tz_config } from '../../Const';
import { CustomerDocumentFileDownload } from './CustomerDocumentFileDownload';

const CustomerDocumentList = () => {
  // 検索条件の初期状態
  const [search_params, setSearchParams] = useState(search_params_init);
  const [datatableDatas, setDattableDatas] = useState<ResultList[]>([]);
  const [is_loading, setIsLoading] = useState(false);
  const [error_message, setErrorMessage] = useState({ ...init_error_message })
  const [is_init, setIsInit] = useState(true);

  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowserBack();
  }, []);

  /**
   * モジュールKWANKGM10050A02をコールする
   */
  const requestModule = async () => {
    try {
      setIsLoading(true);
      setIsInit(false);
      setErrorMessage({ ...init_error_message });

      // POST値の整理
      // KWANKGM10050A02が要求しているPOST値のオブジェクトを作る
      let date_search_item = '';
      if (search_params.date_search.item !== null && typeof search_params.date_search.item !== 'undefined') {
        date_search_item = Moment(search_params.date_search.item).format('YYYY-MM-DD');
      }

      const response = await axios.post(`${location.origin}${process.env.MIX_MOCK_URL}/api/common`, {
        module_name : KWANKGM10050A02,
        is_json : true,
        reception_id : '',
        reception_cd : null,
        date_search: {
          val: {
            target: search_params.date_search.target?.toLocaleDateString(tz_config.jst.lang, tz_config.jst.timezone) ?? '',
            item: search_params.date_search.item,
            type: search_params.date_search.type,
          }
        },
        search: {
          val: {
            target: search_params.search.item === search_target[7].key ? statusSearchStringToId(search_params.search.target) : search_params.search.target,
            item: search_params.search.item,
            type: search_params.search.type,
          }
        }
      });

      if (response.data.response_type === api_response_type.validation || response.data.response_type === api_response_type.error) {
        throw({response: response})
      }

      // 検索結果一覧に表示するためのStateにレスポンスを詰め替える
      const result = refillResponseToResultList(response.data.detail);
      setDattableDatas(result);
    } catch (error) {
      console.error(error.response.data.message);
      // 結果が返ってこない場合の0件の時は一覧を空にする
      if (error.response.data.response_type === api_response_type.error) {
        setDattableDatas([]);
      }
      setErrorMessage(createErrorMessage(error.response.data.detail));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 日付以外による絞り込みの第一条件がステータスの場合、ステータス名をステータスのIDに変換する
   */
  const statusSearchStringToId = (value: string) => {
    // 入力された文字列でステータス名が存在するか検索する
    const status = customer_document_status.find(item => item.value === value);

    // 入力された文字列がステータス名と一致する場合はIDを返却する
    if (typeof status !== 'undefined') {
      return status.id;
    }

    // ステータス名と一致しない場合は入力された文字列をそのまま返却する
    return value;
  };

  /**
   * 検索結果を検索結果一覧に表示するためのオブジェクトに詰め替える
   * @param Object data response.dataのオブジェクト
   * @returns ResultList[] 結果リスト配列
   */
  const refillResponseToResultList = (data: dataKWANKGM10050A02) => {
    const result:ResultList[] = [];
    data.list.map((item, index) => {
      // タイトル
      const title: JSX.Element[] = [];
      if (item.title !== null && typeof item.title !== 'undefined') {
        title.push(<span className="ellipsis" title={item.title} key={`title-${index}`}>{item.title}</span>);
      }

      // ファイル名
      const file_name: JSX.Element[] = [];
      if (item.file_name !== null && typeof item.file_name !== 'undefined') {
        file_name.push(
          <a className="ellipsis" onClick={() => CustomerDocumentFileDownload(item.id, setIsLoading, false)} title={item.file_name} key={`fname-${index}`}>
            {item.file_name}
          </a>
        );
      }

      // 顧客名
      const user_name: JSX.Element[] = [];
      user_name.push(<React.Fragment key={`uname-${index}-1`}>{item.user_list[0].user_name}</React.Fragment>);
      if (item.user_list.length >= 2) {
        user_name.push(<br key={`uname-${index}-2`} />);
        user_name.push(<React.Fragment key={`uname-${index}-3`}>{`他${item.user_list.length - 1}名`}</React.Fragment>);
      }

      // メールアドレス
      const email: JSX.Element[] = [];
      email.push(<React.Fragment key={`email-${index}-1`}>{item.user_list[0].email}</React.Fragment>)
      if (item.user_list.length >= 2) {
        email.push(<br key={`email-${index}-2`} />);
        email.push(<React.Fragment key={`email-${index}-3`}>{`他${item.user_list.length - 1}名`}</React.Fragment>);
      }

      // 詳細ボタン
      const detail: JSX.Element = (
        <Button
          variant="outline-success"
          href={`${location.origin}${process.env.MIX_MOCK_URL}/customer_document/detail?id=${item.id}`}
          className="btn-text-color"
          key={`detail-${index}`}
        >
          詳細
        </Button>
      );

      // ステータスのコード値を表示名に置換する
      const status_name = status[item.status];

      const new_item: ResultList = {
        title: title,
        filename: file_name,
        user_name: user_name,
        email: email,
        start_date: item.start_date,
        end_date: item.end_date,
        registered_person: item.create_user_name,
        authorizer: item.authorizer_name,
        status: status_name,
        updated_at: item.updated_at,
        detail: detail,
      }

      result.push(new_item);
    });
    return result;
  }

  /**
   * エラーメッセージの表示
   * @param Object detail エラーメッセージ
   * 
   */
  const createErrorMessage = (detail: detailKWANKGM10050A02[]): any => {
    const new_error_message = { ...init_error_message };

    if (detail.length === 0) {
      return new_error_message;
    }

    detail.map((item) => {
      if (item.form_name === 'date_search.target') {
        new_error_message.date_search_target = item.message;
      } else if (item.form_name === 'date_search.item') {
        new_error_message.date_search_item = item.message;
      } else if (item.form_name === 'date_search.type') {
        new_error_message.date_search_type = item.message;
      } else if (item.form_name === 'search.target') {
        new_error_message.search_target = item.message;
      } else if (item.form_name === 'search.item') {
        new_error_message.search_item = item.message;
      } else if (item.form_name === 'search.type') {
        new_error_message.search_type = item.message;
      }
    });

    return new_error_message;
  }

  /**
   * 検索ボタンクリック時のイベント処理
   */
  const handcleClickSearchButton = () => {
    requestModule();
  }

  /**
   * 新規追加ボタンクリック時のイベント処理
   */
  const handleClickAdd = () => {
    location.href = `${location.origin}${process.env.MIX_MOCK_URL}/customer_document/edit`;
  }

  /**
   * Select要素を生成する
   * @param KeyValue[] contents Option要素に出力するコンテンツのリスト
   * @param string value 選択値
   * @param React.ChangeEventHandler handleChange onChangeで発火するメソッド
   * @returns JSX.Element Select要素のJSX.Element
   */
  const createSelectJsx = (contents: KeyValue[], value: string, handleChange: React.ChangeEventHandler<HTMLSelectElement>) => {
    return (
      <select
        className="form-control"
        value={value}
        onChange={handleChange}
      >
        {contents.map((content, index) => {
          return(<option value={content.key} key={index}>{content.value}</option>)
        })}
      </select>
    );
  };

  // Select要素を選択した時のイベント
  /**
   * 検索条件-日付検索項目
   * @param React.ChangeEvent event ChangeEventのコールバック
   */
  const handleChangeDateSearchItem = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const new_search_params = { ...search_params };
    new_search_params.date_search.item = event.target.value;
    setSearchParams(new_search_params);
  };
  /**
   * 検索条件-日付検索方法
   * @param React.ChangeEvent event ChangeEventのコールバック
   */
  const handleChangeDateSearchType = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const new_search_params = { ...search_params };
    new_search_params.date_search.type = event.target.value;
    setSearchParams(new_search_params);
  };
  /**
   * 検索条件-検索項目
   * @param React.ChangeEvent event ChangeEventのコールバック
   */
  const handleChangeSearchItem = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const new_search_params = { ...search_params };
    new_search_params.search.item = event.target.value;
    setSearchParams(new_search_params);
  };
  /**
   * 検索条件-検索方法
   * @param React.ChangeEvent event ChangeEventのコールバック
   */
  const handleChangeSearchType = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const new_search_params = { ...search_params };
    new_search_params.search.type = event.target.value;
    setSearchParams(new_search_params);
  };
  // Input要素を操作した時のイベント
  /**
   * 検索年月日
   * @param Date|null date Dateオブジェクト
   */
  const handleChangeDateSearchTarget = (date: Date|null) => {
    const new_search_params = { ...search_params };
    new_search_params.date_search.target = date;
    setSearchParams(new_search_params);
  };
  /**
   * 検索内容
   * @param React.ChangeEvent event ChangeEventのコールバック
   */
  const handleChangeSearchTarget = (event: React.ChangeEvent<HTMLInputElement>) => {
    const new_search_params = { ...search_params };
    new_search_params.search.target = event.target.value;
    setSearchParams(new_search_params);
  };

  return (
    <div className="maintenance">
      <div className="title">顧客宛文書</div>
      <div className="maintenance_container">
        <div className="contents">
          <div id="search-condition">
            {/* 検索条件-日付による絞り込み */}
            <div id="date-target">
              <Row>
                <Col md={2} className="col0">
                  <span>
                    日付による絞り込み
                  </span>
                </Col>
                <Col md={2} className="col1">
                  <>
                    {createSelectJsx(date_search_target, search_params.date_search.item, handleChangeDateSearchItem)}
                    <Messages valid_msg={error_message.date_search_item} />
                  </>
                </Col>
                <Col md={2} className="col2">
                  <>
                    <DatePicker
                      className="form-control"
                      onChange={handleChangeDateSearchTarget}
                      selected={search_params.date_search.target}
                      dateFormat="yyyy-MM-dd"
                      placeholderText=''
                      locale={ja}
                    />
                    <Messages valid_msg={error_message.date_search_target} />
                  </>
                </Col>
                <Col md={2} className="col3">
                  <>
                    {createSelectJsx(date_search_type, search_params.date_search.type, handleChangeDateSearchType)}
                    <Messages valid_msg={error_message.date_search_type} />
                  </>
                </Col>
              </Row>
            </div>
            {/* 検索条件-日付以外による絞り込み */}
            <div id="variable-target">
              <Row>
                <Col md={2} className="col0">
                  <span>
                    日付以外による絞り込み
                  </span>
                </Col>
                <Col md={2} className="col1">
                  <>
                    {createSelectJsx(search_target, search_params.search.item, handleChangeSearchItem)}
                    <Messages valid_msg={error_message.search_item} />
                  </>
                </Col>
                <Col md={2} className="col2">
                  <>
                    <input type="text" className="form-control" placeholder="" onChange={handleChangeSearchTarget} />
                    <Messages valid_msg={error_message.search_target} />
                  </>
                </Col>
                <Col md={2} className="col3">
                  <>
                    {createSelectJsx(search_type, search_params.search.type, handleChangeSearchType)}
                    <Messages valid_msg={error_message.search_type} />
                  </>
                </Col>
                <Col md={1} className="col4">
                  <a className="btn btn-success" onClick={handcleClickSearchButton}>検索</a>
                </Col>
                <Col md={2} className="col5">
                  <a className="btn btn-success" onClick={handleClickAdd}>新規作成</a>
                </Col>
              </Row>
            </div>
            {/* 検索結果表示 */}
            <MaintenanceTable
              datas={datatableDatas}
              columns={datatable_columns}
              params={{
                scrollx: true,
              }}
              is_init={is_init}
            />
            {/* 戻るボタン */}
            <div className="footer-btn">
              <a
                className="btn btn-mini btn-outline-success btn-text-color"
                href={`${location.origin}${process.env.MIX_MOCK_URL}/staff/menu`}
              >
                戻る
              </a>
            </div>
          </div>
        </div>
      </div>
      <LoadingModal show={is_loading} />
    </div>
  );
};

if (document.getElementById('customer-document-list')) {
  render(<CustomerDocumentList />, document.getElementById('customer-document-list'));
}
